import { useEffect, type FC } from 'react'

import { Table } from '@skytech/ui'
import { Td } from '@skytech/ui/Table/Td'
import { Th } from '@skytech/ui/Table/Th'
import { getQueryFilter, getSortedList } from '@skytech/utils'
import { useQuery } from '@tanstack/react-query'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import Highlight from '@/components/Highlight'
import { NoResults } from '@/components/NoResults'
import { ResultCount } from '@/components/ResultCount'
import { useSearch, useSortParams } from '@/hooks/searchParams'
import { useFormattedNumberWithCurrency } from '@/hooks/use-formatted-number-with-currency'
import { useRenderLimit } from '@/hooks/use-render-limit'
import adminQueries from '@/modules/admin/queries'
import type { AccountingReport, ReportFilters } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

import { useReportsContext } from '../index.context'

export const AccountingReports: FC<{
  filters: ReportFilters
}> = ({ filters }) => {
  const { searchFilter } = useSearch()
  const { isReportListLoading, setCount, setIsReportListLoading } = useReportsContext()
  const { locale } = useAppContext()
  const [{ querySort, querySortBy }, { handleSort }] =
    useSortParams<keyof (typeof reportsFiltered)[number]>()

  const { data: reports = [] } = useQuery(
    adminQueries.adminAccountingReports(filters),
    async () => {
      const reports = await adminResources.getAccountingReports(filters)
      setIsReportListLoading(false)
      return reports
    },
    { enabled: Boolean(filters.fromDate && filters.toDate) }
  )

  useEffect(() => {
    setCount(reports.length)
  }, [reports, setCount])

  const stringMatcher = (item: AccountingReport) => [
    item.name,
    `${item.departmentCode} - ${item.departmentName}`,
    item.netAmount,
    item.vatAmount,
    item.totalAmount,
  ]

  const reportsFiltered = reports.filter(reportsObject =>
    getQueryFilter(searchFilter, stringMatcher)(reportsObject)
  )
  const sortedList = getSortedList({
    list: reportsFiltered,
    querySort,
    querySortBy,
    locale,
  })

  const { visibleList, ListLoaderComponent } = useRenderLimit(sortedList, 30)
  const total = reportsFiltered.length
  const { formatNumber } = useFormattedNumberWithCurrency()

  return (
    <>
      <ResultCount count={total} />
      {total > 0 ? (
        <Table>
          <thead>
            <tr>
              <Th onClick={handleSort} id="name">
                <Translation id="Name" />
              </Th>
              <Th onClick={handleSort} id="departmentCode">
                <Translation id="Department" />
              </Th>

              <Th onClick={handleSort} id="netAmount" className="text-right">
                <Translation id="Net Amount" />
              </Th>

              <Th onClick={handleSort} id="vatAmount" className="text-right">
                <Translation id="Vat Amount" />
              </Th>
              <Th onClick={handleSort} id="totalAmount" className="text-right">
                <Translation id="Total Amount" />
              </Th>
            </tr>
          </thead>
          <tbody>
            {visibleList?.map(
              (
                { name, departmentCode, departmentName, netAmount, vatAmount, totalAmount },
                index
              ) => (
                <tr key={`${departmentCode}-${index}`}>
                  <Td>
                    <Highlight text={name} pattern={searchFilter} />
                  </Td>
                  <Td>
                    <Highlight
                      text={`${departmentCode} - ${departmentName}`}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(netAmount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(vatAmount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                  <Td className="text-right">
                    <Highlight
                      text={formatNumber({ amount: Number(totalAmount), precision: 2 })}
                      pattern={searchFilter}
                    />
                  </Td>
                </tr>
              )
            )}
          </tbody>
        </Table>
      ) : (
        !isReportListLoading && <NoResults />
      )}
      <ListLoaderComponent />
    </>
  )
}
